import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import serverFetch from '@globals/ServerFetch';
import getDeviceType from '@globals/utilities/GetDeviceType';
import { PaymentInvoice } from '@customer/types/Payments';

/**
 * Ceny pakietów (z/bez Subskrypcji)
 */
interface PacketListItemPriceData {
	/** Cena dla pakietu z uwzględnieniem rabatu */
	price: [number, number];
	/** Cena dla pakietu bez rabatu */
	priceBeforeDiscount: number;
	/** Cena za punkt dla pakietu z uwzględnieniem rabatu */
	perPoint: [number, number];
	/** Cena za punkt dla pakietu bez rabatu */
	perPointBeforeDiscount: number;
}

/**
 * Pakiet punktów
 */
export interface PacketListItem {
	/** Identyfikator pakietu */
	id: number;
	/** Liczba punktów w pakiecie */
	points: number;
	/** Czy pakiet jest polecany dla użytkownika? */
	isRecommended: boolean;
	/** Liczba miesięcy po których pakiet wygaśnie */
	expiresIn: number;
	/** Data po której pakiet wygaśnie */
	expiresTo: string;
	/** Wielkość promocji w procentach (taka, jaką podał backend) */
	discount: [number, number];
	/** Obliczona wielkość promocji w procentach */
	percentageDiscount: [number, number];
	/** Ilość punktów bonusowych */
	bonusPoints: [number, number];
	/** Cena brutto */
	gross: PacketListItemPriceData;
	/** Cena netto */
	net: PacketListItemPriceData;
	/** Czy cena pakietu jest przeceniona? */
	isDiscounted: [boolean, boolean];
	/** Czy cena za punkt pakietu jest przeceniona? */
	isDiscountedPerPoint: [boolean, boolean];
	/** Czy pakiet jest ukryty? */
	isHidden: boolean;
}

/**
 * Pakiet punktów w postaci backendowej
 */
interface PacketListItemBackend {
	/** Identyfikator pakietu */
	id: number;
	/** Liczba punktów w pakiecie */
	points: number;
	/** Czy pakiet jest polecany dla użytkownika? */
	recommended: boolean;
	/** Liczba miesięcy po których pakiet wygaśnie */
	expiresIn: number;
	/** Data po której pakiet wygaśnie */
	paidPeriodEndDate: string;
	/** Wielkość promocji w procentach (bez Subskrypcji) */
	discount: number;
	/** Wielkość promocji w procentach (z Subskrypcją) */
	discountWithAutomaticPayment: number;
	/** Ilość punktów bonusowych (z Subskrypcją) */
	bonusPoints: number;
	/** Ilość punktów bonusowych (bez Subskrypcji) */
	bonusPointsWithAutomaticPayment: number;
	/** Bazowa cena netto */
	netPriceBeforeDiscount: number | null;
	/** Cena netto (bez Subskrypcji) */
	netPrice: string;
	/** Cena netto (z Subskrypcją) */
	netPriceWithAutomaticPayment: string;
	/** Cena brutto (bez Subskrypcji) */
	price: string;
	/** Cena brutto (z Subskrypcją) */
	priceWithAutomaticPayment: string;
	/** Bazowa cena brutto */
	priceBeforeDiscount: number | null;
	/** Czy pakiet jest ukryty? */
	isHidden: boolean;
}

/**
 * Lista pakietów punktów w postaci backendowej
 */
interface PacketListBackend {
	automaticPayments: boolean;
	/** Czy Tpay jest włączony? */
	tpay: boolean;
	/** Czy Tpay jest włączony dla Subskrypcji? */
	allowTpayRecurringPayment: boolean;
	/** Data zakończenia promocji */
	bonusEndDate: number;
	/** Adres URL do regulaminu promocji */
	tosUrl: string;
	/** Czy zawsze pokazywać pole do wpisania kodu bonusowego? */
	alwaysShowBonusCode: boolean;
	/** Czy obowiązuje bonus do pierwszego doładowania? */
	firstRechargeBonus: boolean;
	/** Domyślna data wygaśnięcia pakietu w miesiącach */
	expiresIn: number;
	/** Lista pakietów */
	data: {
		packets: PacketListItemBackend[];
	};
}

/**
 * Dane do faktury
 */
export interface AccountRechargePaymentData {
	/** Nazwa firmy */
	companyName: string;
	/** Numer NIP */
	nip: {
		country: string;
		value: string;
		isIndividual: boolean;
	};
	/** Ulica */
	street: string;
	/** Adres */
	location: {
		country: [string, string];
		city: [string, string];
		district: [string, string];
		region: [string, string];
	};
}

/**
 * Typy metod płatności
 */
export type AccountRechargePaymentMethodType = 'bank' | 'blik' | 'card' | 'wallet';

/**
 * Pojedyncza metoda płatności w postaci backendowej
 */
interface AccountRechargePaymentMethodBackend {
	id: number;
	name: string
	img: string;
}

/**
 * Metody płatności w postaci backendowej
 */
type AccountRechargePaymentMethodsDataBackend = Record<
	AccountRechargePaymentMethodType, AccountRechargePaymentMethodBackend[]
>;

/**
 * Płatność kartą kredytową
 */
export interface AccountRechargePaymentMethodCard {
	/** Identyfikator płatności */
	id: number;
	/** Numer karty */
	number: { data: string; validation: string; };
	/** Data ważności karty */
	exp: { data: string; validation: string; };
	/** CVV/CVC */
	csc: { data: string; validation: string; };
	/** Operator karty kredytowej */
	provider: string;
	/** Zaszyfrowane dane karty */
	encrypted: string;
}

/**
 * Kod Blik
 */
export interface AccountRechargePaymentMethodBlik {
	/** Wpisany kod */
	data: string;
	/** Identyfikator płatności */
	id: number;
	/** Czy użytkownik ma zapisany kod Blik? */
	hasSaved: boolean;
	/** Czy użyć zapisanego kodu Blik? */
	useSaved: boolean;
	/** Komunikat walidacyjny */
	validation: string;
}

/**
 * Metody płatności
 */
export interface AccountRechargePaymentMethods {
	/** Wybrana metoda płatności */
	selected: AccountRechargePaymentMethodType | '';
	/** Blik */
	blik: AccountRechargePaymentMethodBlik;
	/** Karta kredytowa */
	card: AccountRechargePaymentMethodCard;
	/** Bank */
	bank: {
		selected: number;
		data: AccountRechargePaymentMethodBackend[];
		validation: string;
	};
	/** Portfel elektroniczny */
	wallet: {
		selected: number;
		data: AccountRechargePaymentMethodBackend[];
		validation: string;
	};
}

/**
 * Kod bonusowy
 */
export interface AccountRechargeBonusCode {
	/** Wpisany kod bonusowy */
	value: string;
	/** Identyfikator kodu bonusowego */
	idBonusCode: number;
	/** Ilość przyznanych bonusowych punktów */
	bonusPoints: number;
	/** Status operacji? Jeżeli sukces, wartość wynosi 'ok' */
	validationResult: string;
	/** Komunikat walidacyjny */
	validationMessage: string;
	/** Czy kod bonusowy został aktywowany? */
	isRedeemed: boolean;
	/** Zwrócona przez backend opcjonalna etykietka (najwyższy priorytet) */
	customLabel: string;
	/** Zwrócona przez backend opcjonalna etykietka (dla poszczególnych pakietów) */
	percentageLabels: {
		idRechargeAmount: number,
		customerLabel: string,
	}[];
	/** Cena brutto po zaaplikowaniu kodu bonusowego */
	grossBonusCodePrice: number;
	/** Cena netto po zaaplikowaniu kodu bonusowego */
	netBonusCodePrice: number;
}

export const useAccountRechargeStore = defineStore('accountRecharge', () => {
	const state = {
		/**
		 * Lista wszystkich faktur pobranych z serwera
		 */
		invoices: ref<PaymentInvoice[] | null>(null),
		/**
		 * ID użytkownika potrzebne do analityki
		 */
		customerId: ref(-1),

		/**
		 * ID transakcji pobrane z backendu
		 */
		transactionId: ref(-1),

		/**
		 * Status płatności: `success` jest ustawiany w WebsocketElement!
		 */
		paymentProgress: ref(''),

		/**
		 * Czy Subskrypcja jest włączona?
		 */
		automaticPayments: ref(false),

		/**
		 * Limit Szybkich Doładowań w miesiącu
		 */
		automaticPaymentsLimit: ref(5),

		/**
		 * Maksymalna wartość Szybkich Doładowań = nielimitownaa
		 */
		automaticPaymentsUnlimitedValue: ref(11),

		/**
		 * Czy Tpay jest włączony?
		 */
		isTpayEnabled: ref(false),

		/**
		 * Czy Tpay jest włączony dla Subskrypcji?
		 */
		isTpayEnabledForAutomatic: ref(false),

		/**
		 * Data zakończenia promocji
		 */
		bonusEndDate: ref<Date | undefined>(undefined),

		/**
		 * Adres URL do regulaminu promocji
		 */
		tosUrl: ref(''),

		/**
		 * Czy zawsze pokazywać pole do wpisania kodu bonusowego?
		 */
		alwaysShowBonusCode: ref(false),

		/**
		 * Czy istnieje jakiś pakiet w promocji?
		 */
		hasDiscountedPackets: ref(false),

		/**
		 * Czy obowiązuje bonus do pierwszego doładowania?
		 */
		firstRechargeBonus: ref(false),

		/**
		 * Domyślna data wygaśnięcia pakietu w miesiącach
		 */
		defaultExpiresIn: ref(12),

		/**
		 * Lista pakietów
		 */
		packetList: ref<PacketListItem[]>([]),

		/**
		 * Wybrany pakiet
		 */
		selectedPacket: ref<PacketListItem | undefined>(undefined),

		/**
		 * Jeżeli przychodzimy z ekranu podglądu zapytania, zapisz powrotny adres
		 */
		returnUrl: ref(''),

		/**
		 * Czy załadowaliśmy listę pakietów?
		 */
		isLoaded: ref(false),

		/**
		 * Dane kontaktowe do faktury
		 */
		paymentData: ref<AccountRechargePaymentData | undefined>(undefined),

		/**
		 * Klucz publiczny do zaszyfrowania danych karty
		 */
		pkey: ref(''),

		/**
		 * Host potrzebny do wysłania danych karty
		 */
		host: ref(''),

		/**
		 * Metody płatności
		 */
		paymentMethods: ref<AccountRechargePaymentMethods | undefined>(undefined),

		/**
		 * Kod bonusowy
		 */
		bonusCode: ref<AccountRechargeBonusCode | undefined>(undefined),

		/**
		 * Biblioteka JSEncrypt potrzebna do zaszyfrowania danych karty
		 */
		jsEncrypt: ref<undefined | {
			setPublicKey(key: string): void;
			encrypt(value: string): string;
		}>(undefined),

		/**
		 * Czy blokujemy przyciski akcji (podczas doczytywania danych)?
		 */
		blockButtons: ref(false),
	};

	const actions = {
		/**
		 * Pobiera listę faktur z serwera i przekształca ją na postać frontendową
		 */
		async loadInvoices() {
			let invoiceUrl = '/myoferteo/konto-json/get-invoices.ofer?actionInputJSONString=';
			invoiceUrl += encodeURI(JSON.stringify({ from: '2010-01-01', to: '2038-01-01', page: 1 }));

			const data = (await serverFetch(invoiceUrl)).data as { invoices: any[] };
			state.invoices.value = data.invoices.map((el) => ({
				id: el.id,
				name: el.name,
				date: new Date(el.date),
				dueDate: new Date(el.dueDate),
				paymentUrl: el.paymentUrl,
				amount: el.amount,
				isPaid: el.isPaid,
				isRefunded: el.isRefunded,
				isProcessing: el.isProcessing,
			}) as PaymentInvoice);
		},
		/**
		 * Przekształca informacje o pakiecie punktów z postaci backendowej na frontendową
		 * @param packet Postać backendowa
		 * @returns Postać frontendowa
		 */
		parsePacketListItem(packet: PacketListItemBackend) {
			const bonusPoints = [packet.bonusPoints || 0, packet.bonusPointsWithAutomaticPayment || 0];

			const toNumber = (value: number | string) => Number(String(value).replace(',', '.'));

			const getPercentageDiscount = (value: string) => Math.round((1 - toNumber(value) / toNumber(packet.netPriceBeforeDiscount || packet.netPrice)) * 100);

			const getPrices = (type: 'gross' | 'net') => {
				const getPerPoint = (price = [0, 0], points = 0, bonusPoints = [0, 0]) => [
					price[0] / (points + bonusPoints[0]),
					price[1] / (points + bonusPoints[1]),
				];

				let price = [0, 0];
				let priceBeforeDiscount = 0;

				if (type === 'gross') {
					price = [toNumber(packet.price), toNumber(packet.priceWithAutomaticPayment)];
					priceBeforeDiscount = toNumber(packet.priceBeforeDiscount || packet.price);
				} else {
					price = [toNumber(packet.netPrice), toNumber(packet.netPriceWithAutomaticPayment)];
					priceBeforeDiscount = toNumber(packet.netPriceBeforeDiscount || packet.netPrice);
				}

				return {
					price,
					priceBeforeDiscount,
					perPoint: getPerPoint(price, packet.points, bonusPoints),
					perPointBeforeDiscount: packet.points > 0 ? priceBeforeDiscount / packet.points : 0,
				} as PacketListItemPriceData;
			};

			const result = {
				id: packet.id,
				points: packet.points,
				isRecommended: packet.recommended,
				expiresIn: packet.expiresIn,
				expiresTo: packet.paidPeriodEndDate,
				discount: [packet.discount || 0, packet.discountWithAutomaticPayment || 10],
				percentageDiscount: [
					getPercentageDiscount(packet.netPrice),
					getPercentageDiscount(packet.netPriceWithAutomaticPayment),
				],
				bonusPoints,
				gross: getPrices('gross'),
				net: getPrices('net'),
				isDiscounted: [false, false],
				isDiscountedPerPoint: [false, false],
				isHidden: packet.isHidden,
			} as PacketListItem;

			return {
				...result,
				isDiscounted: [
					result.net.price[0] < result.net.priceBeforeDiscount,
					result.net.price[1] < result.net.priceBeforeDiscount,
				] as [boolean, boolean],
				isDiscountedPerPoint: [
					result.net.perPoint[0] < result.net.perPointBeforeDiscount,
					result.net.perPoint[1] < result.net.perPointBeforeDiscount,
				] as [boolean, boolean],
			};
		},

		/**
		 * Pobiera listę pakietów punktów oraz ustawia niezbędne pola
		 * @returns Czy operacja się udała?
		 */
		async loadPacketList() {
			state.selectedPacket.value = undefined;

			const rsvp = (await serverFetch('/myoferteo/konto-json/get-price-list.ofer')) as PacketListBackend;
			if (!rsvp || !rsvp.data?.packets) { return; }

			state.automaticPayments.value = rsvp.automaticPayments;
			state.isTpayEnabled.value = rsvp.tpay;
			state.isTpayEnabledForAutomatic.value = rsvp.allowTpayRecurringPayment;
			state.bonusEndDate.value = rsvp.bonusEndDate > 0 ? new Date(rsvp.bonusEndDate) : undefined;
			state.tosUrl.value = rsvp.tosUrl || '';
			state.alwaysShowBonusCode.value = rsvp.alwaysShowBonusCode;
			state.firstRechargeBonus.value = rsvp.firstRechargeBonus || false;
			state.defaultExpiresIn.value = rsvp.expiresIn || 12;

			state.packetList.value = rsvp.data?.packets.map((packet) => actions.parsePacketListItem(packet));

			// Jeżeli przy pierwszej płatności backend nie zwróci rekomendowanego pakietu, wybierz drugi najtańszy
			if (!state.packetList.value.find((packet) => packet.isRecommended)) {
				state.packetList.value[1].isRecommended = true;
			}

			state.hasDiscountedPackets.value = state.packetList.value.some((packet) => packet.isDiscounted[0]);

			state.isLoaded.value = true;
		},

		/**
		 * W przypadku, gdy przychodzimy ze strony np. zlecenia, zapisujemy
		 * wcześniej w sessionStorage adres, do którego będzie można powrócić
		 * po dokonaniu płatności.
		 * @returns Czy był zapisany adres powrotny?
		 */
		getReturnUrl() {
			const returnUrlKey = 'accountRechargeReturnUrl';
			if (window.sessionStorage.getItem(returnUrlKey)) {
				state.returnUrl.value = window.sessionStorage.getItem(returnUrlKey) || '';
				window.sessionStorage.removeItem(returnUrlKey);
				return true;
			}
			return false;
		},

		/**
		 * Ustawia wybrany pakiet punktów do użycia w przyszłości,
		 * czyści kod bonusowy oraz ładuje dane dla ekranu podsumowania.
		 * @param packet Wybrany pakiet punktów
		 * @returns Czy akcja się udała?
		 */
		async selectPacket(packet: PacketListItem) {
			state.selectedPacket.value = packet;
			state.bonusCode.value = actions.getBonusCode();
			state.blockButtons.value = true;
			const result = await actions.loadSummaryData();
			state.blockButtons.value = false;
			return result;
		},

		/**
		 * Tworzy obiekt dla kodów bonusowych
		 * @returns Czysty kod bonusowy
		 */
		getBonusCode() {
			return {
				value: '',
				idBonusCode: 0,
				bonusPoints: 0,
				validationResult: '',
				validationMessage: '',
				isRedeemed: false,
				customLabel: '',
				percentageLabels: [] as {
					idRechargeAmount: number,
					customerLabel: string,
				}[],
				grossBonusCodePrice: 0,
				netBonusCodePrice: 0,
			};
		},

		/**
		 * Pobiera dane dla ekranu podsumowania, jeżeli nie zostały one wcześniej pobrane.
		 * W innym przypadku czyści tylko sekcję metod płatności.
		 * @returns Dane dla ekranu podsumowania
		 */
		async loadSummaryData() {
			if (state.paymentData.value && state.paymentMethods.value) {
				// Jeżeli włączona Subskrypcja, ustaw płatność na kartę
				state.paymentMethods.value.selected = state.automaticPayments.value ? 'card' : '';

				// Wyczyść kod Blik
				state.paymentMethods.value.blik.data = '';
				state.paymentMethods.value.blik.validation = '';
				return true;
			}

			const rsvp = await serverFetch('/myoferteo/konto-json/get-account-recharge-summary.ofer');
			if (rsvp.status !== 'OK') { return false; }

			state.pkey.value = rsvp.pkey;
			state.host.value = rsvp.host;
			state.paymentData.value = actions.parsePaymentData(rsvp.data);
			state.paymentMethods.value = actions.parsePaymentMethods(rsvp.paymentMethods, rsvp.blikSaved);
			return true;
		},

		/**
		 * Przekształca informacje o danych do faktury z postaci backendowej na frontendową
		 * @param data Postać backendowa
		 * @returns Postać frontendowa
		 */
		parsePaymentData(data: AccountRechargePaymentData) {
			return {
				companyName: data.companyName || '',
				nip: {
					country: data.nip.country || 'PL',
					value: (data.nip.value || '').replace(/\D/g, ''),
					isIndividual: data.nip.isIndividual || false,
				},
				street: data.street || '',
				location: {
					country: data.location.country || ['locationPl', 'Polska'],
					city: [data.location.city[0] || '', data.location.city[1] || ''] as [string, string],
					district: data.location.district || ['', ''],
					region: data.location.region || ['', ''],
				},
			};
		},

		/**
		 * Przekształca informacje o metodach płatności z postaci backendowej na frontendową
		 * @param methods Postać backendowa
		 * @param blikSaved  Czy blik został zapisany?
		 * @returns Postać frontendowa
		 */
		parsePaymentMethods(methods: AccountRechargePaymentMethodsDataBackend, blikSaved = false) {
			return {
				selected: state.automaticPayments.value ? 'card' : '' as AccountRechargePaymentMethodType,
				blik: {
					data: '',
					id: methods.blik[0].id,
					hasSaved: blikSaved,
					useSaved: false,
					validation: '',
				},
				card: {
					id: methods.card[0].id,
					number: { data: '', validation: '' },
					exp: { data: '', validation: '' },
					csc: { data: '', validation: '' },
					provider: '',
					encrypted: '',
				},
				bank: {
					selected: 0,
					data: methods.bank,
					validation: '',
				},
				wallet: {
					selected: 0,
					data: methods.wallet,
					validation: '',
				},
			};
		},

		/**
		 * Tworzy obiekt dla backendu z danymi adresowymi do faktury
		 * @param data Obiekt źródłowy
		 * @returns Obiekt dla backendu
		 */
		getPaymentDataPayload(data: AccountRechargePaymentData) {
			return {
				id: state.selectedPacket.value?.id,
				automaticPayments: state.automaticPayments.value,
				sourceTrack: 'accountBalance',
				invoiceData: {
					companyName: data.companyName,
					nip: data.nip,
					street: data.street,
					location: {
						country: data.location.country,
						region: data.location.region,
						district: data.location.district,
						city: data.location.city,
					},
				},
			};
		},

		/**
		 * Zapisuje dane adresowe do faktury
		 * @param data Obiekt źródłowy
		 * @returns Czy operacja się udała?
		 */
		async savePaymentData(data: AccountRechargePaymentData) {
			const url = '/myoferteo/konto-json/save-payment-data.ofer';
			const rsvp = await serverFetch(url, {
				actionInputJSONString: JSON.stringify(actions.getPaymentDataPayload(data)),
			}, 'POST');

			if (rsvp.status !== 'OK') { return false; }
			state.paymentData.value = data;
			return true;
		},

		/**
		 * Pobiera obiekt do parametrów zdarzenia GTM.
		 * @param eventName Nazwa zdarzenia
		 */
		sendAnalytics(eventName: string) {
			const packet = state.selectedPacket.value;
			if (!packet) { return { eventName: '' }; }

			const finalEventName = (eventName === 'purchase_points') ? eventName : `accountRecharge_${eventName}`;

			const data = {
				id_customer: state.customerId.value,
				is_dotpay: !state.isTpayEnabled.value,
				event_source: 'accountBalance',
				rfpId: '',
				packet_id: packet.id,
				value: packet.gross.price[state.automaticPayments.value ? 1 : 0],
				quantity: packet.points,
				automatic_payments_enabled: state.automaticPayments.value,
				currency: 'PLN',
			} as Record<string, string | boolean | number>;

			if (eventName === 'complete_success' || eventName === 'payment' || eventName === 'purchase_points') {
				data['id_purchase_points'] = state.transactionId.value;
			}

			return { eventName: finalEventName, eventData: data };
		},

		/**
		 * Wysyła informacje analityczne do bazy danych.
		 * @param step Nazwa zdarzenia
		 */
		sendAnalyticsDatabase(step: string) {
			const packet = state.selectedPacket.value;
			const methods = state.paymentMethods.value;

			if (!packet || !methods) { return; }

			if (step === 'packetChoice') {
				fetch(`/fee/user-activity-app.ofer?amountId=${packet.id}&deviceType=${getDeviceType()}`);
			}

			// step -> enum [pricingList, summary, paymentMethod, pay, correctPayment, failedPayment, tryAgain]
			let params = `paymentTrackDTO.step=${step}&paymentTrackDTO.automaticPayments=${state.automaticPayments.value}`;
			params += `&paymentTrackDTO.idRechargeAmount=${packet.id}`;

			try {
				if (methods.selected) {
					let methodId;
					if (methods.selected === 'bank' || methods.selected === 'wallet') {
						methodId = methods[methods.selected].selected;
					} else if (methods.selected === 'blik' || methods.selected === 'card') {
						methodId = methods[methods.selected].id;
					}
					if (methodId) { params += `&paymentTrackDTO.groupId=${methodId}`; }
				}
			} catch (err) {
				console.warn('TpaySendAnalytics', err);
			}

			params += `&paymentTrackDTO.deviceType=${getDeviceType()}`;
			serverFetch(`/myoferteo/konto-json/log-payment-track.ofer?${params}`);
		},

		/**
		 * Pobiera i inicjalizuje bibliotekę JSEncrypt, potrzebną do zaszyfrowania danych karty.
		 */
		async jsEncryptInit() {
			if (!state.jsEncrypt.value) {
				const a = '/scripts/_/';
				const b = 'jsencrypt.min.js';
				await import(/* @vite-ignore */ a + b);

				// @ts-expect-error: no typings for JSEncrypt.
				state.jsEncrypt.value = new JSEncrypt();
				if (!state.jsEncrypt.value) { return; }
				state.jsEncrypt.value.setPublicKey(atob(state.pkey.value));
			}
		},

		/**
		 * Szyfruje przy użyciu JSEncrypt dane karty, by operator płatności mógł je przyjąć.
		 * Postać przed zaszyfrowaniem wygląda tak: 371676285681407|12/2012|323|oferteo.pl
		 * @returns Zaszyfrowane dane karty
		 */
		encryptCard() {
			if (!state.paymentMethods.value) { return ''; }
			if (!state.jsEncrypt.value) {
				throw new Error('No jsEncrypt library!');
			}

			const { card } = state.paymentMethods.value;
			const expDateSplit = card.exp.data.replace('.', '/').split('/');
			const expDate = `${expDateSplit[0]}/20${expDateSplit[1]}`;
			const cardDataToEncrypt = `${card.number.data.replace(/[^0-9]/g, '')}|${expDate}|${card.csc.data}|${state.host.value}`;
			return state.jsEncrypt.value.encrypt(cardDataToEncrypt);
		},

		/**
		 * Czy powinniśmy otworzyć okno płatności od razu po kliknięciu w przycisk?
		 * Niektóre przeglądarki blokują wyskakujące okna, jeżeli trzeba chwilę
		 * poczekać - w tym przypadku na pobranie adresu strony banku.
		 * @param selected Wybrana metoda płatności
		 * @returns Czy okno powinno być otwarte wcześniej?
		 */
		shouldOpenWindow(selected: AccountRechargePaymentMethodType) {
			const UA = navigator.userAgent;
			const isFirefox = /firefox/i.test(UA);
			const isSafari = /^((?!chrome|android).)*safari/i.test(UA) && (typeof openNewWindow === 'undefined');
			if (selected === 'blik') { return false; }
			return isFirefox || isSafari;
		},

		/**
		 * Tworzy obiekt dla backendu z informacjami o wybranej metodzie płatności
		 * @param selected Wybrana metoda płatności
		 * @returns Obiekt dla backendu
		*/
		getPaymentMethodPayload(selected: AccountRechargePaymentMethodType) {
			let payload = {} as Record<string, string | number | boolean>;
			if (!state.paymentMethods.value) { return payload; }

			if (selected === 'bank' || selected === 'wallet') {
				const method = state.paymentMethods.value[selected];
				payload = {
					method: selected,
					groupId: method.selected,
				};
			}

			if (selected === 'card') {
				const method = state.paymentMethods.value[selected];
				payload = {
					method: selected,
					groupId: method.id,
					cardEncrypted: actions.encryptCard(),
				};
			}

			if (selected === 'blik') {
				const method = state.paymentMethods.value[selected];
				payload = {
					method: selected,
					groupId: method.id,
					cardEncrypted: actions.encryptCard(),
					blikToken: !method.useSaved ? method.data : '',
					useSaved: method.useSaved,
				};
			}

			return payload;
		},

		/**
		 * Tworzy obiekt dla backendu z danymi całej płatności
		 * @returns Obiekt dla backendu
		 */
		getPaymentPayload() {
			if (!state.paymentMethods.value?.selected) {
				throw new Error('No payment method selected!');
			}
			if (!state.paymentData.value) {
				throw new Error('No payment data loaded!');
			}

			const { selected } = state.paymentMethods.value;
			const shouldOpenWindow = actions.shouldOpenWindow(selected);

			const automaticPaymentsLimit =
				state.automaticPaymentsLimit.value === state.automaticPaymentsUnlimitedValue.value
					? -1
					: state.automaticPaymentsLimit.value;

			const payload = {
				paymentData: {
					...actions.getPaymentDataPayload(state.paymentData.value),
					automaticPaymentsLimit,
				},
				paymentMethod: actions.getPaymentMethodPayload(selected),
				testPayment: false,
				paymentToken: '',
			} as Record<string, string | number | boolean | object>;

			if (state.bonusCode.value?.isRedeemed) {
				payload.bonusPoints = state.bonusCode.value.bonusPoints;
				payload.bonusCodeValue = state.bonusCode.value.value;
			}

			state.transactionId.value = 0;
			state.paymentProgress.value = 'progress';
			return { shouldOpenWindow, payload };
		},

		/**
		 * Próbuje dokonać akcję płatności.
		 * @param actionInputJSONString Dane dal backendu
		 * @returns W zależności od statusu operacji:
		 * - { status: 'ERROR' }, gdy wystąpił nieznany błąd
		 * - { status: 'VALIDATION' }, gdy wystąpił błąd walidacji
		 * - { status: 'OK', url }, gdy wszystko się udało.
		 *   Opcjonalnie przekazywany jest adres strony banku
		 */
		async doPayment(actionInputJSONString: string) {
			state.blockButtons.value = true;
			const rsvp = await serverFetch(
				'/myoferteo/konto-json/buy-packet-tpay.ofer',
				{ actionInputJSONString },
				'POST',
			);
			state.blockButtons.value = false;

			// Wystąpił błąd z blikiem.
			if (state.paymentMethods.value && state.paymentMethods.value.selected === 'blik' && rsvp.status === 'ERROR') {
				// Wystąpił błąd z zapisanym kodem.
				if ((rsvp.messages || ['']).join('').includes('Podany alias nie został odnaleziony')) {
					return { status: 'ERROR' };
				}

				if (state.paymentMethods.value) {
					state.paymentMethods.value.blik.data = '';
					state.paymentMethods.value.blik.validation = 'Błędny kod BLIK. Spróbuj ponowanie';
				}
				return { status: 'VALIDATION_BLIK' };
			}

			// Wystąpił nieznany błąd.
			if (rsvp.status === 'ERROR') {
				return { status: 'ERROR' };
			}

			// Wystąpił błąd walidacyjny.
			const { validationErrors: validation } = rsvp;
			if (state.paymentMethods.value && validation) {
				if (validation.blik) {
					state.paymentMethods.value.blik.validation = validation.blik;
				}
				if (validation.card) {
					state.paymentMethods.value.card.number.validation = validation.card;
				}
				return { status: 'VALIDATION' };
			}

			// Wszystko gra, można ruszyć
			state.transactionId.value = rsvp.data?.idPurchasePoints;
			return {
				status: 'OK',
				url: btoa(rsvp.data?.tpayURL || ''),
			};
		},

		/**
		 * Przygotowuje dane do zdarzenia zakupu punktów
		 * @returns Dane zdarzenia
		 */
		getPurchasePointsEventData() {
			const packet = state.selectedPacket.value;
			if (!packet) { return undefined; }
			return {
				packet_id: packet.id,
				value: packet.gross.price[state.automaticPayments.value ? 1 : 0],
				quantity: packet.points,
				automatic_payments_enabled: state.automaticPayments.value,
				currency: 'PLN',
				id_purchase_points: state.transactionId.value,
			};
		},
	};

	const getters = {
		/**
		 * Warunek wyświetlania baneru ReferalProgramBox.vue. Wyświetla się tylko Wykonawcom, którzy opłacili dwie faktury lub więcej
		 */
		showReferralProgramBanner: computed(()=> {
			const result = state?.invoices?.value?.filter((invoice) => invoice.isPaid).length >= 2;
			return result ?? false;	
		}),
	};

	return { ...state, ...actions, ...getters };
});
